/**
 * MethodologyDetailView — `/templates/methodologies/$methodologyId` (OT-0055).
 *
 * Una metodología: su contenido agrupado por tipo (ciclo, artefactos, roles,
 * flujos, incluidos los genéricos que usa) y el reporte de completitud del
 * backend (OT-0053) con enlaces a lo que falta, y el de calidad de sus
 * plantillas (OT-0006). No recalcula nada: los miembros y los problemas
 * salen de `GET /methodologies/{id}/completeness` y `.../quality`.
 *
 * El admin edita, publica (solo completa), archiva, borra (sin plantillas
 * propias) y la elige como predeterminada de la organización. Las del
 * producto son de solo lectura, salvo elegirlas como predeterminada.
 */

import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AlertCircle, CheckCircle2, Info } from 'lucide-react'

import {
  archiveMethodology,
  deleteMethodology,
  getMethodology,
  getMethodologyCompleteness,
  getMethodologyQuality,
  publishMethodology,
  setTenantDefaultMethodology,
  type CompletenessIssue,
  type CompletenessReport,
  type Methodology,
  type MethodologyMember,
} from '../api/methodologies'
import { isForbiddenTier } from '../api/client'
import { retryUnlessForbiddenTier } from '../api/queryRetry'
import { listTemplates, type Template, type TemplateType } from '../api/templates'
import { useAccessToken } from '../auth/useAccessToken'
import { useMe } from '../auth/useMe'
import { ConfirmDialog, type ConfirmTone } from '../components/ConfirmDialog'
import { BackButton } from '../components/shell/BackButton'
import { ScrollPage } from '../components/shell/ScrollPage'
import {
  MethodologyDefaultBadges,
  MethodologyStatusChip,
} from '../components/templates/methodologies/MethodologyBadges'
import { MethodologyFormDialog } from '../components/templates/methodologies/MethodologyFormDialog'
import { NewTemplateDialog } from '../components/templates/NewTemplateDialog'
import {
  QualityFindingList,
  QualitySummary,
} from '../components/templates/quality/QualityFindingList'
import { useApiErrorMessage } from '../hooks/useApiErrorMessage'
import { METHODOLOGIES_QUERY_KEY } from '../hooks/useMethodologies'
import { canManageTenant } from '../lib/tiers'
import { pushToast } from '../stores/toastStore'
import { DEFAULT_LOCALE } from '../i18n/config'
import { NoAccessView } from './NoAccessView'

type MemberGroup = 'lifecycle' | 'artifact' | 'role' | 'approval_workflow'
const GROUP_ORDER: MemberGroup[] = ['lifecycle', 'artifact', 'role', 'approval_workflow']

type Action = 'publish' | 'archive' | 'delete' | 'setDefault' | 'unsetDefault'

export function MethodologyDetailView() {
  const { methodologyId } = useParams({
    from: '/templates/methodologies/$methodologyId',
  })
  const { t, i18n } = useTranslation('templates')
  const { accessToken, loading: authLoading } = useAccessToken()
  const { me } = useMe()
  const navigate = useNavigate()
  const apiErrorMessage = useApiErrorMessage()

  const methodologyQuery = useQuery({
    queryKey: [...METHODOLOGIES_QUERY_KEY, methodologyId, i18n.language],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return getMethodology(accessToken, methodologyId)
    },
    enabled: !!accessToken,
    retry: retryUnlessForbiddenTier,
  })
  const completenessQuery = useQuery({
    queryKey: [...METHODOLOGIES_QUERY_KEY, methodologyId, 'completeness'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return getMethodologyCompleteness(accessToken, methodologyId)
    },
    enabled: !!accessToken,
    retry: retryUnlessForbiddenTier,
  })
  // Los miembros del reporte traen clave y tipo; el nombre localizado sale
  // del catálogo (misma query que la vista de Plantillas sin filtros).
  const templatesQuery = useQuery({
    queryKey: ['templates', 'catalog', i18n.language, 'all', ''],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listTemplates(accessToken)
    },
    enabled: !!accessToken,
    retry: retryUnlessForbiddenTier,
  })
  const templatesById = useMemo(
    () => new Map((templatesQuery.data ?? []).map((tpl) => [tpl.id, tpl] as const)),
    [templatesQuery.data],
  )

  const backToList = () =>
    void navigate({ to: '/templates', search: { tab: 'methodologies' } })

  const error = methodologyQuery.error ?? completenessQuery.error
  const methodology = methodologyQuery.data
  const report = completenessQuery.data

  if (isForbiddenTier(error)) return <NoAccessView />

  return (
    <ScrollPage>
      <div className="max-w-5xl">
        <BackButton
          fallback={backToList}
          className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink mb-4"
        >
          {t('methodologies.detail.back')}
        </BackButton>
        {authLoading || methodologyQuery.isLoading || completenessQuery.isLoading ? (
          <p className="font-mono text-xs text-ink-subtle">
            {t('methodologies.detail.loading')}
          </p>
        ) : error || !methodology || !report ? (
          <div className="rounded-md border border-critical/40 bg-critical-soft p-4 text-xs text-critical">
            {apiErrorMessage(error)}
          </div>
        ) : (
          <MethodologyDetail
            methodology={methodology}
            report={report}
            templatesById={templatesById}
            canManage={canManageTenant(me?.role, me?.tenant?.access)}
            onDeleted={backToList}
          />
        )}
      </div>
    </ScrollPage>
  )
}

function MethodologyDetail({
  methodology,
  report,
  templatesById,
  canManage,
  onDeleted,
}: {
  methodology: Methodology
  report: CompletenessReport
  templatesById: ReadonlyMap<string, Template>
  canManage: boolean
  onDeleted: () => void
}) {
  const { t } = useTranslation('templates')
  const { accessToken } = useAccessToken()
  const queryClient = useQueryClient()
  const apiErrorMessage = useApiErrorMessage()
  const [pending, setPending] = useState<Action | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [newTemplateOpen, setNewTemplateOpen] = useState(false)

  const isProduct = methodology.tenant_id === null
  const isArchived = methodology.status === 'archived'
  const writable = canManage && !isProduct && !isArchived
  const ownCount = report.members.filter((m) => m.origin === 'methodology').length

  const mutation = useMutation({
    mutationFn: async (action: Action) => {
      if (!accessToken) throw new Error('no_access_token')
      switch (action) {
        case 'publish':
          return publishMethodology(accessToken, methodology.id)
        case 'archive':
          return archiveMethodology(accessToken, methodology.id)
        case 'delete':
          return deleteMethodology(accessToken, methodology.id)
        case 'setDefault':
          return setTenantDefaultMethodology(accessToken, methodology.id)
        case 'unsetDefault':
          return setTenantDefaultMethodology(accessToken, null)
      }
    },
    onSuccess: (_, action) => {
      const name = methodology.name
      const toast: Record<Action, string> = {
        publish: t('methodologies.actions.toast.published', { name }),
        archive: t('methodologies.actions.toast.archived', { name }),
        delete: t('methodologies.actions.toast.deleted', { name }),
        setDefault: t('methodologies.actions.toast.default_set', { name }),
        unsetDefault: t('methodologies.actions.toast.default_unset'),
      }
      pushToast({ kind: 'success', message: toast[action] })
      setPending(null)
      if (action === 'delete') {
        queryClient.removeQueries({
          queryKey: [...METHODOLOGIES_QUERY_KEY, methodology.id],
        })
        onDeleted()
      }
      void queryClient.invalidateQueries({ queryKey: METHODOLOGIES_QUERY_KEY })
    },
    onError: (err) => {
      pushToast({ kind: 'critical', message: apiErrorMessage(err) })
      setPending(null)
      // Un 422 al publicar trae el reporte vigente: refrescarlo.
      void queryClient.invalidateQueries({ queryKey: METHODOLOGIES_QUERY_KEY })
    },
  })

  const confirm: Record<Action, { title: string; body: string; tone: ConfirmTone }> = {
    publish: {
      title: t('methodologies.actions.publish_confirm_title', { name: methodology.name }),
      body: t('methodologies.actions.publish_confirm_body'),
      tone: 'positive',
    },
    archive: {
      title: t('methodologies.actions.archive_confirm_title', { name: methodology.name }),
      body: t('methodologies.actions.archive_confirm_body'),
      tone: 'warning',
    },
    delete: {
      title: t('methodologies.actions.delete_confirm_title', { name: methodology.name }),
      body: t('methodologies.actions.delete_confirm_body'),
      tone: 'danger',
    },
    setDefault: {
      title: t('methodologies.actions.set_default_confirm_title'),
      body: t('methodologies.actions.set_default_confirm_body', { name: methodology.name }),
      tone: 'neutral',
    },
    unsetDefault: {
      title: t('methodologies.actions.set_default_confirm_title'),
      body: t('methodologies.actions.unset_default_confirm_body'),
      tone: 'neutral',
    },
  }

  return (
    <>
      <header className="mb-6">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle">
            {t('methodologies.detail.eyebrow')}
          </span>
          <span className="text-[10px] font-mono text-ink-subtle">
            {methodology.code} · v{methodology.version} ·{' '}
            {isProduct
              ? t('methodologies.owner_product')
              : t('methodologies.owner_tenant')}
          </span>
          <MethodologyStatusChip status={methodology.status} />
          <MethodologyDefaultBadges methodology={methodology} />
        </div>
        <h1 className="font-display text-3xl text-ink">{methodology.name}</h1>
        {methodology.description ? (
          <p className="mt-2 font-body text-sm text-ink-muted max-w-3xl">
            {methodology.description}
          </p>
        ) : null}

        {canManage ? (
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            {writable ? (
              <ActionButton kind="ghost" onClick={() => setNewTemplateOpen(true)}>
                {t('methodologies.actions.new_template')}
              </ActionButton>
            ) : null}
            {writable ? (
              <ActionButton kind="ghost" onClick={() => setEditOpen(true)}>
                {t('methodologies.actions.edit')}
              </ActionButton>
            ) : null}
            {writable && methodology.status === 'draft' ? (
              <ActionButton
                kind="primary"
                disabled={!report.is_complete}
                onClick={() => setPending('publish')}
              >
                {t('methodologies.actions.publish')}
              </ActionButton>
            ) : null}
            {methodology.status === 'published' && !methodology.is_tenant_default ? (
              <ActionButton kind="ghost" onClick={() => setPending('setDefault')}>
                {t('methodologies.actions.set_default')}
              </ActionButton>
            ) : null}
            {methodology.is_tenant_default ? (
              <ActionButton kind="ghost" onClick={() => setPending('unsetDefault')}>
                {t('methodologies.actions.unset_default')}
              </ActionButton>
            ) : null}
            {writable ? (
              <ActionButton kind="ghost" onClick={() => setPending('archive')}>
                {t('methodologies.actions.archive')}
              </ActionButton>
            ) : null}
            {canManage && !isProduct && ownCount === 0 ? (
              <ActionButton kind="danger" onClick={() => setPending('delete')}>
                {t('methodologies.actions.delete')}
              </ActionButton>
            ) : null}
          </div>
        ) : null}
        {writable && methodology.status === 'draft' && !report.is_complete ? (
          <p className="mt-2 text-[11px] text-ink-subtle">
            {t('methodologies.actions.publish_disabled_hint')}
          </p>
        ) : null}
      </header>

      {isProduct ? (
        <Notice>{t('methodologies.detail.read_only_product')}</Notice>
      ) : isArchived ? (
        <Notice>{t('methodologies.detail.archived_notice')}</Notice>
      ) : null}

      <CompletenessSection report={report} />
      <QualitySection methodologyId={methodology.id} report={report} />
      <ContentSection report={report} templatesById={templatesById} />

      <MethodologyFormDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        methodology={methodology}
      />
      <NewTemplateDialog
        open={newTemplateOpen}
        onClose={() => setNewTemplateOpen(false)}
        defaultMethodologyId={methodology.id}
      />
      {pending ? (
        <ConfirmDialog
          open
          tone={confirm[pending].tone}
          title={confirm[pending].title}
          body={confirm[pending].body}
          confirmLabel={t('methodologies.actions.confirm')}
          cancelLabel={t('methodologies.actions.cancel')}
          busy={mutation.isPending}
          onCancel={() => !mutation.isPending && setPending(null)}
          onConfirm={() => mutation.mutate(pending)}
        />
      ) : null}
    </>
  )
}

// ─── Completitud ───────────────────────────────────────────────────────────
function CompletenessSection({ report }: { report: CompletenessReport }) {
  const { t } = useTranslation('templates')
  const keyToId = useMemo(
    () => new Map(report.members.map((m) => [m.template_key, m.template_id] as const)),
    [report.members],
  )
  return (
    <section className="mb-8">
      <h2 className="font-display text-lg text-ink mb-3">
        {t('methodologies.detail.completeness_title')}
      </h2>
      {report.is_complete ? (
        <div className="flex items-start gap-2 rounded-md border border-official/30 bg-official-soft p-3 text-sm text-official">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          <p>{t('methodologies.detail.complete')}</p>
        </div>
      ) : (
        <div className="rounded-md border border-critical/30 bg-critical-soft p-3">
          <div className="flex items-start gap-2 text-sm text-critical">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <p>
              {t('methodologies.detail.incomplete', { count: report.issues.length })}
            </p>
          </div>
          <ul className="mt-2 space-y-1.5 pl-6">
            {report.issues.map((issue, i) => (
              <IssueItem
                key={i}
                issue={issue}
                templateId={
                  issue.template_key ? keyToId.get(issue.template_key) : undefined
                }
              />
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

function IssueItem({
  issue,
  templateId,
}: {
  issue: CompletenessIssue
  templateId: string | undefined
}) {
  const { t } = useTranslation('templates')
  const message = t(`methodologies.detail.issues.${issue.kind}`, {
    template_key: issue.template_key ?? '',
    reference: issue.reference ?? '',
  })
  return (
    <li className="text-xs text-ink list-disc">
      {templateId ? (
        <Link
          to="/templates/$templateId"
          params={{ templateId }}
          className="underline decoration-line-strong underline-offset-2 hover:text-brand"
        >
          {message}
        </Link>
      ) : (
        message
      )}
      {issue.location ? (
        <span className="ml-1 font-mono text-[10px] text-ink-subtle">
          {t('methodologies.detail.issue_location', { location: issue.location })}
        </span>
      ) : null}
    </li>
  )
}

// ─── Calidad ───────────────────────────────────────────────────────────────
function QualitySection({
  methodologyId,
  report,
}: {
  methodologyId: string
  report: CompletenessReport
}) {
  const { t } = useTranslation('templates')
  const { accessToken } = useAccessToken()
  const qualityQuery = useQuery({
    queryKey: [...METHODOLOGIES_QUERY_KEY, methodologyId, 'quality'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return getMethodologyQuality(accessToken, methodologyId)
    },
    enabled: !!accessToken,
    retry: retryUnlessForbiddenTier,
  })
  const keyToId = useMemo(
    () => new Map(report.members.map((m) => [m.template_key, m.template_id] as const)),
    [report.members],
  )
  const quality = qualityQuery.data
  return (
    <section className="mb-8">
      <h2 className="font-display text-lg text-ink mb-3">{t('quality.title')}</h2>
      {qualityQuery.isError ? (
        <p className="text-xs text-ink-subtle">{t('quality.load_error')}</p>
      ) : !quality ? (
        <p className="font-mono text-xs text-ink-subtle">{t('quality.loading')}</p>
      ) : quality.findings.length === 0 ? (
        <div className="flex items-start gap-2 rounded-md border border-official/30 bg-official-soft p-3 text-sm text-official">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          <p>{t('quality.clean')}</p>
        </div>
      ) : (
        <div className="rounded-md border border-line bg-surface p-3">
          <p className="text-sm mb-1">
            <QualitySummary findings={quality.findings} />
          </p>
          <p className="text-xs text-ink-muted leading-relaxed mb-3">{t('quality.intro')}</p>
          <QualityFindingList findings={quality.findings} templateIdByKey={keyToId} />
        </div>
      )}
    </section>
  )
}

// ─── Contenido ─────────────────────────────────────────────────────────────
function ContentSection({
  report,
  templatesById,
}: {
  report: CompletenessReport
  templatesById: ReadonlyMap<string, Template>
}) {
  const { t, i18n } = useTranslation('templates')
  const groups = useMemo(() => {
    const byType = new Map<TemplateType, MethodologyMember[]>()
    for (const member of report.members) {
      const list = byType.get(member.template_type) ?? []
      list.push(member)
      byType.set(member.template_type, list)
    }
    const nameOf = (m: MethodologyMember) =>
      templatesById.get(m.template_id)?.name ?? m.template_key
    return GROUP_ORDER.map((type) => ({
      type,
      members: (byType.get(type) ?? []).sort(
        (a, b) =>
          Number(a.origin === 'generic') - Number(b.origin === 'generic') ||
          nameOf(a).localeCompare(nameOf(b), i18n.language || DEFAULT_LOCALE),
      ),
    })).filter((g) => g.members.length > 0)
  }, [report.members, templatesById, i18n.language])
  const hasGeneric = report.members.some((m) => m.origin === 'generic')

  return (
    <section>
      <h2 className="font-display text-lg text-ink mb-3">
        {t('methodologies.detail.content_title')}
      </h2>
      {groups.length === 0 ? (
        <div className="rounded-md border border-dashed border-line bg-surface p-6 text-center">
          <p className="font-body text-sm text-ink-muted">
            {t('methodologies.detail.content_empty')}
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {hasGeneric ? (
            <p className="text-[11px] text-ink-subtle">
              {t('methodologies.detail.generic_hint')}
            </p>
          ) : null}
          {groups.map((group) => (
            <div key={group.type}>
              <h3 className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle mb-1.5">
                {t(`methodologies.detail.groups.${group.type}`)} · {group.members.length}
              </h3>
              <ul className="bg-surface border border-line rounded-md divide-y divide-line/60">
                {group.members.map((member) => (
                  <MemberRow
                    key={member.template_id}
                    member={member}
                    template={templatesById.get(member.template_id)}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function MemberRow({
  member,
  template,
}: {
  member: MethodologyMember
  template: Template | undefined
}) {
  const { t } = useTranslation('templates')
  return (
    <li>
      <Link
        to="/templates/$templateId"
        params={{ templateId: member.template_id }}
        className="flex items-center gap-3 px-3 py-2 hover:bg-surface-muted transition-colors"
      >
        <span className="text-sm text-ink truncate">
          {template?.name ?? member.template_key}
        </span>
        <span className="font-mono text-[10px] text-ink-subtle truncate mr-auto">
          {member.template_key}
          {template?.current_version ? ` · v${template.current_version}` : ''}
        </span>
        {member.origin === 'generic' ? (
          <span className="px-1.5 py-0.5 rounded text-[9px] font-medium uppercase tracking-wider bg-surface-muted text-ink-muted">
            {t('methodologies.detail.origin_generic')}
          </span>
        ) : null}
        {member.status !== 'published' ? (
          <span className="px-1.5 py-0.5 rounded text-[9px] font-medium uppercase tracking-wider bg-surface-muted text-ink-subtle">
            {t(`status.${member.status}` as 'status.draft', {
              defaultValue: member.status,
            })}
          </span>
        ) : null}
      </Link>
    </li>
  )
}

// ─── Helpers ───────────────────────────────────────────────────────────────
function Notice({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-6 flex items-start gap-2 rounded-md border border-line bg-surface p-3 text-xs text-ink-muted">
      <Info className="w-4 h-4 mt-0.5 shrink-0" />
      <p>{children}</p>
    </div>
  )
}

function ActionButton({
  kind,
  disabled,
  onClick,
  children,
}: {
  kind: 'primary' | 'ghost' | 'danger'
  disabled?: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  const className = {
    primary: 'bg-brand text-canvas hover:bg-brand-deep',
    ghost: 'border border-line text-ink-muted hover:text-ink hover:bg-surface-muted',
    danger: 'border border-critical/40 text-critical hover:bg-critical-soft',
  }[kind]
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      {children}
    </button>
  )
}
